import React from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { useEvaluationStore } from '../../store/evaluationStore';
import type { EvaluationLineProps } from '../../types/evaluation';

// 配置済みアイテム用のドラッグ可能コンポーネント
const DraggableEvaluationItem: React.FC<{
  productIndex: number;
  criterionIndex: number;
  product: any;
  position: { x: number; y: number };
  onDoubleClick?: (productIndex: number) => void;
}> = ({ productIndex, criterionIndex, product, position, onDoubleClick }) => {
  const { removeEvaluationPosition } = useEvaluationStore();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `placed-${productIndex}`,
    data: {
      type: 'placed-item',
      productIndex,
      criterionIndex,
    },
  });  const style = {
    left: `${position.x}%`,
    top: `${position.y * 18 + 40}px`, // エリア拡大に合わせて間隔を調整（14px → 18px）+ 初期オフセット40px
    transform: transform 
      ? `translate3d(${transform.x}px, ${transform.y}px, 0) translate(-50%, -50%)` 
      : 'translateX(-50%)', // ドラッグ中もそうでなくても中央基準
    opacity: isDragging ? 0.5 : 1, // ドラッグ中は半透明
  };
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDoubleClick) {
      onDoubleClick(productIndex);
    }
  };

  return (
    <div
      ref={setNodeRef}
      className="evaluation-line__item"
      style={style}
      {...listeners}
      {...attributes}
      onDoubleClick={handleDoubleClick}
      title="ダブルクリックで詳細を表示"
    >
      <div className="evaluation-line__item-content">
        <span className="evaluation-line__item-title">{product.reviewTitle}</span>
        <button 
          className="evaluation-line__item-remove" 
          title="配置を解除"
          onPointerDown={(e) => {
            e.stopPropagation(); // ドラッグ開始を防ぐ
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            // 評価位置を削除して未配置状態に戻す
            removeEvaluationPosition(productIndex, criterionIndex);
          }}
        >
          &times;
        </button>
      </div>
    </div>
  );
};

export const EvaluationLine = React.forwardRef<HTMLDivElement, EvaluationLineProps>(
  ({ criterionIndex, products, getPosition, onProductDoubleClick }, ref) => {
    const { setEvaluationPosition } = useEvaluationStore();

    // ドロップゾーンとしての設定
    const {
      setNodeRef,
      isOver,
    } = useDroppable({
      id: 'evaluation-line',
      data: {
        criterionIndex,
      },
    });

    // refを設定する関数
    const setRefs = (element: HTMLDivElement | null) => {
      setNodeRef(element);
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    return (
      <div 
        ref={setRefs}
        className={`evaluation-line ${isOver ? 'evaluation-line--over' : ''}`}
      >
        <div className="evaluation-line__track" />
        
        {/* 配置済みの評価対象を表示 */}
        {products.map((product, productIndex) => {
          const position = getPosition(productIndex, criterionIndex);
          if (!position) return null;
          
          return (            <DraggableEvaluationItem
              key={productIndex}
              productIndex={productIndex}
              criterionIndex={criterionIndex}
              product={product}
              position={position}
              onDoubleClick={onProductDoubleClick}
            />
          );
        })}
      </div>
    );
  }
);